import * as React from 'react'
import { DialogContent, DialogOverlay, DialogPortal } from './dialog'
import { cn } from '@/lib/utils'
import { Icons } from '@/theme/Images'

const BottomSheetDialog = React.forwardRef(
  ({ 
    className, 
    overlayClassName, 
    onClose, 
    closable = true, 
    children, 
    title,
    footerContent,
    ...props 
  }, ref) => {
    const [isMobile, setIsMobile] = React.useState(false)

    // Detect mobile screen size
    React.useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 768) // md breakpoint
      }
      
      checkMobile()
      window.addEventListener('resize', checkMobile)
      
      return () => window.removeEventListener('resize', checkMobile)
    }, [])

    // Mobile bottom sheet layout
    if (isMobile) {
      return (
        <DialogPortal>
          <DialogOverlay 
            className={cn('fixed inset-0 z-50 bg-black/80', overlayClassName)} 
            onClick={onClose}
          />
          <div
            ref={ref}
            className={cn(
              'fixed inset-x-0 bottom-0 z-50 bg-white shadow-lg max-h-[90vh] flex flex-col rounded-t-xl',
              className
            )}
            style={{ 
              width: '100vw',
              borderBottomLeftRadius: '0px',
              borderBottomRightRadius: '0px'
            }}
            {...props}
          >
            {/* Scrollable Content */}
            <div 
              className="flex-1 overflow-y-auto relative"
              // Prevent touch, wheel, and scroll events from bubbling up
              // to ensure that scrolling within the dialog content works correctly
              onTouchStart={(e) => {
                e.stopPropagation()
              }}
              onTouchMove={(e) => {
                e.stopPropagation()
              }}
              onWheel={(e) => {
                e.stopPropagation()
              }}
            >
              {children}
              {closable ? (
                <button
                  id="dialog-close-button"
                  aria-label="Close"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (onClose) {
                      onClose(false)
                    }
                  }}
                  className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-50"
                >
                  <img src={Icons.closeIcon} className="h-6 w-6" />
                  <span className="sr-only">Close</span>
                </button>
              ) : null}
            </div>

            {/* Fixed Footer */}
            {footerContent && (
              <div className="border-t border-gray-200 bg-white flex-shrink-0">
                {footerContent}
              </div>
            )}
          </div>
        </DialogPortal>
      )
    }

    // Desktop/standard layout - use regular Dialog
    const handleClose = (e) => {
      if (e) {
        e.stopPropagation()
      }
      onClose(e)
    }
    
    return (
      <DialogContent
        ref={ref}
        className={className}
        overlayClassName={overlayClassName}
        onClose={handleClose}
        closable={closable}
        {...props}
      >
        {children}
        {/* Add footerContent to desktop layout */}
        {footerContent && (
          <div>
            {footerContent}
          </div>
        )}
      </DialogContent>
    )
  }
)

BottomSheetDialog.displayName = 'BottomSheetDialog'

export { BottomSheetDialog }
